import {
  GAME_ID,
  REDUX_ACTIONS,
  NO_TAG,
  UNTAG_OPTION,
  SIMPLE_FEEDBACK_THRESHOLD,
} from '../../data/constants';

const initialState = {
  currentTaggingMode: NO_TAG, // current tag mode selected
  currentlyTaggedGroups: {},
  foundStoryElements: [], // all story elements that the player has found so far. Does this belong in roundPlayReducer now?
  currentWordLayouts: [],
  pendingWordLayouts: [],
  wordCountInItem: 0,
  pendingWordCount: 0,
  disabledTaggingModes: [],
  currentHighlighetedMode: NO_TAG, // current tag mode highlighted for onboarding
  areQuestionMarksHighlighted: false,
  phrasesToAutoComplete: [], // an array of phrases that an AI controlled character will need to tag because the user is struggling.
};

const taggableParagraphReducer = (state = initialState, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  switch (type) {
    case REDUX_ACTIONS.SET_LEVEL_ID:
      return {
        ...initialState,
      };

    case REDUX_ACTIONS.INITIALIZE_ROUND:
      return {
        ...state,
        foundStoryElements: payload.foundStoryElements,
      };

    case REDUX_ACTIONS.SELECT_TAGGING_MODE:
      return {
        ...state,
        currentTaggingMode: payload,
        currentHighlighetedMode: NO_TAG,
        areQuestionMarksHighlighted: false,
      };

    case REDUX_ACTIONS.HIGHLIGHT_TAGGING_MODE:
      return {
        ...state,
        currentHighlighetedMode: payload,
      };

    case REDUX_ACTIONS.SET_HIGHLIGHT_QUESTION_MARKS:
      return {
        ...state,
        areQuestionMarksHighlighted: payload,
      };

    case REDUX_ACTIONS.DISABLE_TAGGING_OPTION:
      return {
        ...state,
        disabledTaggingModes: [...state.disabledTaggingModes, payload.nameInCurriculum],
      };

    case REDUX_ACTIONS.SELECT_WORD_GROUP: {
      if (state.currentTaggingMode === NO_TAG) {
        return state;
      }
      const currentlyTaggedGroups = { ...state.currentlyTaggedGroups };
      const { groupId, isCorrect, tagsLeftToCollect } = payload;

      if (state.currentTaggingMode.text === UNTAG_OPTION.text) {
        // if we are currently in "untag" mode ...
        currentlyTaggedGroups[groupId] = undefined;
      } else {
        // if we are not in "untag" mode...
        const currentGroup = currentlyTaggedGroups[groupId];
        const canUpdateTag = (
          (tagsLeftToCollect > 0 && currentGroup === undefined) ||
          !!currentGroup && (currentGroup.text !== state.currentTaggingMode.text)
        );
        if (canUpdateTag) {
          currentlyTaggedGroups[groupId] = { ...state.currentTaggingMode, isCorrect };
        }
      }
      return {
        ...state,
        currentlyTaggedGroups,
      };
    }

    case REDUX_ACTIONS.SELECT_RESPONSE: {
      const { isCorrect, incorrectSubmissionsCount } = payload;
      // update state if we were correct, or if we triggered negative feedback
      if (isCorrect || (!isCorrect && incorrectSubmissionsCount + 1 >= SIMPLE_FEEDBACK_THRESHOLD)) {
        // remove any "found" story elements from the current question. They'll be replaced in just a moment.
        const foundStoryElements = state.foundStoryElements.filter(e => e.questionId !== payload.questionId);
        const currentlyTaggedGroups = { ...state.currentlyTaggedGroups };
        const keys = Object.keys(state.currentlyTaggedGroups);
        keys.forEach((key) => {
          if (currentlyTaggedGroups[key] !== undefined) {
            if (currentlyTaggedGroups[key].isCorrect) {
              // "lock in" any correct answers so that you can't click on them again to remove them.
              foundStoryElements.push({ questionId: payload.questionId, groupId: key });
            } else {
              // automatically untag any answer choices that are not correct.
              currentlyTaggedGroups[key] = undefined;
            }
          }
        });

        return {
          ...state,
          foundStoryElements,
          currentlyTaggedGroups,
        };
      }
      return state;
    }

    case REDUX_ACTIONS.SET_UP_AUTO_COMPLETE: {
      const foundStoryElements = state.foundStoryElements.filter(e => e.questionId !== payload.questionId);
      const currentlyTaggedGroups = { ...state.currentlyTaggedGroups };

      const keys = Object.keys(state.currentlyTaggedGroups);
      keys.forEach((key) => {
        if (currentlyTaggedGroups[key] !== undefined) {
          if (currentlyTaggedGroups[key].isCorrect) {
            // "lock in" any correct answers so that you can't click on them again to remove them.
            foundStoryElements.push({ questionId: payload.questionId, groupId: key });
          } else {
            // automatically untag any answer choices that are not correct.
            currentlyTaggedGroups[key] = undefined;
          }
        }
      });
      return {
        ...state,
        foundStoryElements,
        currentlyTaggedGroups,
      };
    }

    case REDUX_ACTIONS.NEXT_STANDARD_ITEM:
      return payload.isLeavingFinalQuestion
        ? state // don't reset the paragraph when leaving the final question in the level.
        : {
          ...state,
          areQuestionMarksHighlighted: false,
          currentHighlighetedMode: NO_TAG,
          currentTaggingMode: NO_TAG,
          currentlyTaggedGroups: {},
          currentWordLayouts: [],
          pendingWordLayouts: [],
          disabledTaggingModes: [],
          phrasesToAutoComplete: [],
        };

    case REDUX_ACTIONS.SET_WORD_COUNT_IN_ITEM: {
      return {
        ...state,
        wordCountInItem: payload,
        pendingWordCount: 0,
      };
    }

    case REDUX_ACTIONS.REGISTER_TAGGABLE_WORD_LAYOUT: {
      const pendingWordLayouts = [...state.pendingWordLayouts];
      const { groupIdAndIndex: { groupId, groupIndex }, layout: { x, y, width, height } } = payload;

      const newGroup = { groupId, rows: [] };
      if (pendingWordLayouts[groupIndex] !== undefined) {
        newGroup.rows = [...pendingWordLayouts[groupIndex].rows];
      }

      const affectedRowIndex = newGroup.rows.findIndex(e => e.y === y);

      if (affectedRowIndex < 0) {
        newGroup.rows.push({ x, y, width, height });
      } else {
        const newRow = newGroup.rows[affectedRowIndex];
        newGroup.rows[affectedRowIndex] = {
          ...newRow,
          width: newRow.width + width,
          x: Math.min(newRow.x, x),
        };
      }

      pendingWordLayouts[groupIndex] = newGroup;

      const currentWordLayouts = state.pendingWordCount + 1 >= state.wordCountInItem
        ? pendingWordLayouts
        : state.currentWordLayouts;

      return {
        ...state,
        pendingWordLayouts,
        pendingWordCount: state.pendingWordCount + 1,
        currentWordLayouts,
      };
    }

    case REDUX_ACTIONS.START_EXPLICIT_FEEDBACK:
      return {
        ...state,
        phrasesToAutoComplete: payload,
      };

    case REDUX_ACTIONS.END_OF_GUIDED_SEQUENCE:
      if (payload === 'youSureAboutThat') {
        return {
          ...state,
          currentTaggingMode: NO_TAG,
        };
      }
      return state;

    default:
      return state;
  }
};

export default taggableParagraphReducer;
